/**
 * The overlay-trigger snippets a merchant copies from the dashboard (URL, HTML
 * and React link/button, and the AI prompt). Pure string building, kept out of
 * `OverlaySnippets.tsx` so it can be unit-tested without rendering.
 *
 * PE-2084 dk-4: the BUTTON snippets use the queue-stub handler from
 * `overlayButtonSnippet.ts`, so a tap before the embed bundle arrives is kept.
 */
import { contrastTextColor } from '@charitystack/shared';
import { buildOverlayElementURL, normalizeDomain } from './urlElement';
import {
  overlayButtonHtmlOnclick,
  overlayButtonJsHandler,
  overlayButtonReactOnClick,
} from './overlayButtonSnippet';

export type OverlayType = 'donate' | 'attend';

export interface Tab {
  key: string;
  label: string;
  snippet: string;
  /** Top-level group this tab belongs to */
  group?: string;
}

export interface GetTabsOptions {
  elementID: string;
  /** The connected form ID. Used alongside elementID in url-element variant. */
  formID?: string;
  type: OverlayType;
  /** Raw merchant domain (e.g. "example.org" or "https://example.org"). */
  domain?: string;
  /** Raw page path (e.g. "donate-now" or "/landing/page"); empty for site root. */
  pagePath?: string;
  color?: string;
  /** When true, generates URLs with both formid and elementid params. */
  useElementIDFormat?: boolean;
}

export function getTabs({
  elementID,
  formID,
  type,
  domain,
  pagePath,
  color,
  useElementIDFormat,
}: GetTabsOptions): Tab[] {
  const actionLabel = type === 'donate' ? 'Donate' : 'Register';
  const btnStyle = color
    ? ` style="background-color: ${color}; color: ${contrastTextColor(
        color,
      )}; border: 1px solid rgba(30,30,30,0.15); padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 16px; font-weight: 600; font-family: 'Open Sans', sans-serif; box-shadow: 0 1px 1px 0 rgba(0,0,0,0.25); transition: all 200ms ease-in-out;"`
    : '';
  const linkStyle = color ? ` style="color: ${color}; text-decoration: underline;"` : '';

  // Single source of truth for the overlay URL string. Shared with URLBuilderDrawer
  // so the saved generatedURL on a URL element always matches what merchants copy.
  // form variant: ?{type}&formid={elementID-acting-as-formID}
  // url-element variant: ?{type}&formid={formID}&elementid={elementID}
  const url = buildOverlayElementURL({
    domain,
    pagePath,
    type,
    formID: useElementIDFormat ? formID || elementID : elementID,
    elementID: useElementIDFormat ? elementID : undefined,
  });
  const normalizedDomain = normalizeDomain(domain);
  // PE-2084 dk-4: the openOverlay arguments, as JS literals, for the button
  // snippets' queue-stub handler (see overlayButtonSnippet.ts). Same positions
  // as before — the signature is frozen. With useElementIDFormat the type is
  // always passed so elementID lands in the 3rd position.
  const callArgs = useElementIDFormat
    ? [formID || elementID, type, elementID]
    : type === 'attend'
      ? [elementID, type]
      : [elementID];
  const htmlOnclick = overlayButtonHtmlOnclick(callArgs);
  // The Prompt tab presents this AS JavaScript, so it gets the raw handler; the
  // attribute-escaped form belongs only inside onclick="…".
  const jsHandler = overlayButtonJsHandler(callArgs);
  const reactOnClick = overlayButtonReactOnClick(callArgs);

  return [
    {
      key: 'url',
      label: 'URL',
      group: 'url',
      snippet: url,
    },
    {
      key: 'html-link',
      label: 'Link',
      group: 'html',
      snippet: `<a href="${url}"${linkStyle}>${actionLabel}</a>`,
    },
    {
      key: 'html-button',
      label: 'Button',
      group: 'html',
      snippet: `<button onclick="${htmlOnclick}"${btnStyle}>${actionLabel}</button>`,
    },
    {
      key: 'react-link',
      label: 'Link',
      group: 'react',
      snippet: color
        ? `<a href="${url}" style={{ color: '${color}', textDecoration: 'underline' }}>${actionLabel}</a>`
        : `<a href="${url}">${actionLabel}</a>`,
    },
    {
      key: 'react-button',
      label: 'Button',
      group: 'react',
      snippet: color
        ? `<button onClick={${reactOnClick}} style={{ backgroundColor: '${color}', color: '${contrastTextColor(
            color,
          )}', border: '1px solid rgba(30,30,30,0.15)', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', fontFamily: "'Open Sans', sans-serif", boxShadow: '0 1px 1px 0 rgba(0,0,0,0.25)', transition: 'all 200ms ease-in-out' }}>${actionLabel}</button>`
        : `<button onClick={${reactOnClick}}>${actionLabel}</button>`,
    },
    {
      key: 'prompt',
      label: 'Prompt',
      group: 'prompt',
      snippet: useElementIDFormat
        ? `I want to add a CharityStack ${
            type === 'donate' ? 'donation' : 'registration'
          } overlay trigger to my website.${
            normalizedDomain ? `\nMy website URL: ${normalizedDomain}` : ''
          }\n\nPrerequisite — CharityStack embed script:\nFirst, check if the page already has a <script> tag loading from CharityStack (e.g. charitystack.com or a CharityStack CDN). If the script is NOT present, the overlay will not work — ask me to copy the embed script from my CharityStack dashboard (Settings → Embed) and add it to the page before continuing.\n\nBefore writing any code, ask me whether I want:\n- A button (opens the overlay via JavaScript)\n- A link (navigates to the overlay URL)\n\nThen use the option I choose.\n\nButton implementation — use this exact click handler; it also works when the button is tapped before the CharityStack script has finished loading: ${jsHandler}\nLink URL: ${url}\n\nAdditional notes:\n- Do NOT add a second CharityStack script tag if one already exists.${
            color
              ? `\n- Style with brand color ${color} as background and ${contrastTextColor(
                  color,
                )} as text color.`
              : ''
          }\n- In React/JSX, use window.CharityStack instead of CharityStack.`
        : `I want to add a CharityStack ${
            type === 'donate' ? 'donation' : 'registration'
          } overlay trigger to my website.${
            normalizedDomain ? `\nMy website URL: ${normalizedDomain}` : ''
          }\n\nPrerequisite — CharityStack embed script:\nFirst, check if the page already has a <script> tag loading from CharityStack (e.g. charitystack.com or a CharityStack CDN). If the script is NOT present, the overlay will not work — ask me to copy the embed script from my CharityStack dashboard (Settings → Embed) and add it to the page before continuing.\n\nBefore writing any code, ask me whether I want:\n- A button (opens the overlay via JavaScript)\n- A link (navigates to the overlay URL)\n\nThen use the option I choose.\n\nButton implementation — use this exact click handler; it also works when the button is tapped before the CharityStack script has finished loading: ${jsHandler}\nLink URL: ${url}\n\nAdditional notes:\n- Do NOT add a second CharityStack script tag if one already exists.${
            color
              ? `\n- Style with brand color ${color} as background and ${contrastTextColor(
                  color,
                )} as text color.`
              : ''
          }\n- In React/JSX, use window.CharityStack instead of CharityStack.`,
    },
  ];
}
